import { ScrollArea } from '@mantine/core';
import { FC, ReactNode } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import CircularLoader from '@/components/CircularLoader.tsx';
import DroppableComponent from '@/components/DroppableComponent.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { cn } from '@/utils/styleUtils.ts';

interface Props {
    id: string;
    title: string;
    fallbackText: string;
    children: ReactNode[];
    classNames?: {
        wrapper?: string;
    };
    infiniteScroll?: {
        totalDataLength: number;
        dataLength: number;
        hasMore: boolean;
        loadMore: () => void;
    };
}

// TODO: use in room view columns
const Column: FC<Props> = ({ id, title, fallbackText, children, classNames, infiniteScroll }) => {
    const scrollable_id = `scrollable-${id}`;

    const renderContent = (): ReactNode => (
        <DroppableComponent id={id} className="m-2">
            <div
                className={cn('mx-1 space-y-2 rounded-lg', children.length === 0 && 'bg-slate-50 dark:bg-neutral-700')}
            >
                {children.length === 0 && (
                    <Paragraph className="p-4 text-center text-slate-500 dark:text-neutral-300">
                        {fallbackText}
                    </Paragraph>
                )}
                {children}
            </div>
        </DroppableComponent>
    );

    return (
        <div className={cn('mx-2 flex h-full flex-col', classNames?.wrapper)}>
            <Paragraph className="mx-3 mb-4 text-sm tracking-wider text-slate-500 xl:text-base">
                {title}{' '}
                {infiniteScroll
                    ? infiniteScroll.totalDataLength > 0 && `(${children.length}/${infiniteScroll.totalDataLength})`
                    : children.length > 0 && `(${children.length})`}
            </Paragraph>

            {infiniteScroll ? (
                <div id={scrollable_id} className="h-full overflow-auto">
                    <InfiniteScroll
                        dataLength={infiniteScroll.dataLength}
                        next={infiniteScroll.loadMore}
                        hasMore={infiniteScroll.hasMore}
                        loader={
                            <div className="flex justify-center py-4">
                                <CircularLoader />
                            </div>
                        }
                        scrollableTarget={scrollable_id}
                        className="m-2"
                    >
                        {renderContent()}
                    </InfiniteScroll>
                </div>
            ) : (
                <ScrollArea
                    scrollHideDelay={0}
                    classNames={{
                        root: 'h-full',
                        scrollbar: 'opacity-10',
                        viewport: '[&>div]:!block',
                    }}
                >
                    {/*TODO: remove margin when we move outlined element to outer component*/}
                    {renderContent()}
                </ScrollArea>
            )}
        </div>
    );
};

export default Column;
